import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  Save,
  Eye,
  EyeOff,
  Settings,
  Lock
} from 'lucide-react';
import axios from '../../utils/api';
import { useMasterRecords } from '../../contexts/MasterRecordsContext';
import toast from 'react-hot-toast';
import Modal from '../../components/Modal/Modal';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';

const AdminPortalMasterRecords = () => {
  const [localRecords, setLocalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    recordType: 'STATUS',
    name: '',
    displayName: '',
    description: '',
    isActive: true,
    sortOrder: 0,
    colorCode: ''
  });
  
  const { masterRecords, refreshMasterRecords, isRecordEditable } = useMasterRecords();

  // Admin Portal-specific record types
  const recordTypes = [
    { value: 'ALL', label: 'All Types' },
    { value: 'STATUS', label: 'Lead Status' },
    { value: 'PRIORITY', label: 'Priority' },
    { value: 'ASSIGNED_TO', label: 'Assigned To' },
    { value: 'SOURCE', label: 'Lead Source' }
  ];

  useEffect(() => {
    fetchMasterRecords();
  }, []);

  const fetchMasterRecords = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/master-records/admin-portal');
      setLocalRecords(response.data);
    } catch (error) {
      console.error('Error fetching master records:', error);
      toast.error('Failed to fetch master records');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = () => {
    setEditingRecord(null);
    setFormData({
      recordType: 'STATUS',
      name: '',
      displayName: '',
      description: '',
      isActive: true,
      sortOrder: 0,
      colorCode: ''
    });
    setShowModal(true);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setFormData({
      recordType: record.recordType,
      name: record.name,
      displayName: record.displayName || '',
      description: record.description || '',
      isActive: record.isActive,
      sortOrder: record.sortOrder || 0,
      colorCode: record.colorCode || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRecord) {
        await axios.put(`/api/master-records/${editingRecord.id}`, formData);
        toast.success('Master record updated successfully');
      } else {
        await axios.post('/api/master-records', formData);
        toast.success('Master record created successfully');
      }
      setShowModal(false);
      fetchMasterRecords();
      refreshMasterRecords(); // Refresh global context
      
      // Refresh dashboard if available
      if (window.refreshAdminDashboard) {
        window.refreshAdminDashboard();
      }
    } catch (error) {
      console.error('Error saving master record:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save master record';
      toast.error(errorMessage);
    }
  };

  const handleDeleteRecord = (record) => {
    setDeletingRecord(record);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteRecord = async () => {
    if (!deletingRecord) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/master-records/${deletingRecord.id}`);
      toast.success('Master record deleted successfully');
      fetchMasterRecords();
      refreshMasterRecords(); // Refresh global context
      
      // Refresh dashboard if available
      if (window.refreshAdminDashboard) {
        window.refreshAdminDashboard();
      }
      
      setShowDeleteConfirm(false);
      setDeletingRecord(null);
    } catch (error) {
      console.error('Error deleting master record:', error);
      toast.error('Failed to delete master record');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingRecord(null);
    setDeleteLoading(false);
  };

  const filteredRecords = localRecords.filter(record => {
    const matchesSearch = record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (record.displayName && record.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = selectedType === 'ALL' || record.recordType === selectedType;
    return matchesSearch && matchesType;
  });

  const groupedRecords = filteredRecords.reduce((acc, record) => {
    if (!acc[record.recordType]) {
      acc[record.recordType] = [];
    }
    acc[record.recordType].push(record);
    return acc;
  }, {});

  const getTypeColor = (type) => {
    const colors = {
      STATUS: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300',
      PRIORITY: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300',
      ASSIGNED_TO: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300',
      SOURCE: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        {/* <div className="flex items-center gap-3 mb-2">
          <Settings className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Admin Portal Master Records</h1>
        </div> */}
        <p className="text-gray-600 dark:text-gray-400">Manage admin portal-specific master data like lead status, priority, and assignees</p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            >
              {recordTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchMasterRecords}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleAddRecord}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              Add Record
            </button>
          </div>
        </div>
      </div>

      {/* Records by Type */}
      <div className="space-y-6">
        {Object.keys(groupedRecords).length === 0 ? (
          <div className="text-center py-12">
            <Settings className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">No admin portal master records found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm">Create your first record to get started</p>
          </div>
        ) : (
          Object.entries(groupedRecords).map(([type, records]) => (
            <motion.div
              key={type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 capitalize">
                  {type.replace('_', ' ').toLowerCase()} ({records.length})
                </h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {records.map((record) => (
                    <motion.div
                      key={record.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow dark:bg-gray-900/50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(record.recordType)}`}>
                            {record.recordType.replace('_', ' ')}
                          </span>
                          <span className={`flex items-center gap-1 text-xs ${record.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {record.isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            {record.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {isRecordEditable(record.recordType, record.name) ? (
                            <button
                              onClick={() => handleEditRecord(record)}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          ) : (
                            <div className="p-1 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="This record cannot be edited">
                              <Lock className="w-4 h-4" />
                            </div>
                          )}
                          {isRecordEditable(record.recordType, record.name) && (
                            <button
                              onClick={() => handleDeleteRecord(record)}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{record.name}</h4>
                      {record.displayName && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{record.displayName}</p>
                      )}
                      {record.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-500">{record.description}</p>
                      )}
                      {record.colorCode && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-500">Color:</span>
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600"
                            style={{ backgroundColor: record.colorCode }}
                          ></div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingRecord ? 'Edit Admin Portal Master Record' : 'Add Admin Portal Master Record'}
        size="medium"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Record Type *
              </label>
              <select
                value={formData.recordType}
                onChange={(e) => setFormData({ ...formData, recordType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                required
              >
                <option value="STATUS">Lead Status</option>
                <option value="PRIORITY">Priority</option>
                <option value="ASSIGNED_TO">Assigned To</option>
                <option value="SOURCE">Lead Source</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Color Code
              </label>
              <input
                type="color"
                value={formData.colorCode}
                onChange={(e) => setFormData({ ...formData, colorCode: e.target.value })}
                className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingRecord ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDeleteRecord}
        title="Delete Master Record"
        message={`Are you sure you want to delete "${deletingRecord?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminPortalMasterRecords;
